import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const defaultCardPageSizeOptions = [9, 18, 36];

type PaginatedCardGridProps = {
    children: React.ReactNode;
    emptyMessage: string;
    hasItems: boolean;
    isLoading: boolean;
    itemLabel: string;
    pageIndex: number;
    pageSize: number;
    totalItems: number;
    onPageIndexChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    renderSkeleton: (index: number) => React.ReactNode;
    className?: string;
    contentClassName?: string;
    emptyClassName?: string;
    filterSlot?: React.ReactNode;
    gridClassName?: string;
    itemLabelPlural?: string;
    pageSizeOptions?: number[];
};

export function PaginatedCardGrid({
    children,
    emptyMessage,
    hasItems,
    isLoading,
    itemLabel,
    pageIndex,
    pageSize,
    totalItems,
    onPageIndexChange,
    onPageSizeChange,
    renderSkeleton,
    className = '',
    contentClassName = '',
    emptyClassName = '',
    filterSlot,
    gridClassName = 'grid grid-cols-5 gap-x-[1.55vw] gap-y-[2.8vh]',
    itemLabelPlural,
    pageSizeOptions = defaultCardPageSizeOptions,
}: PaginatedCardGridProps) {
    const [displayPageSize, setDisplayPageSize] = React.useState(pageSize);
    const lastPageSizePropRef = React.useRef(pageSize);
    const labelPlural = itemLabelPlural || `${itemLabel}s`;
    const totalPages = Math.max(1, Math.ceil(totalItems / displayPageSize));
    const pageStart = totalItems ? ((pageIndex - 1) * displayPageSize) + 1 : 0;
    const pageEnd = Math.min(pageIndex * displayPageSize, totalItems);
    const pageNumbers = React.useMemo(() => {
        const start = Math.max(1, Math.min(pageIndex - 2, totalPages - 4));
        const end = Math.min(totalPages, start + 4);

        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }, [pageIndex, totalPages]);

    React.useEffect(() => {
        if (lastPageSizePropRef.current !== pageSize) {
            lastPageSizePropRef.current = pageSize;
            setDisplayPageSize(pageSize);
        }
    }, [pageSize]);

    React.useEffect(() => {
        if (!pageSizeOptions.includes(displayPageSize) && pageSizeOptions.length > 0) {
            const fallbackPageSize = pageSizeOptions[0];
            lastPageSizePropRef.current = fallbackPageSize;
            setDisplayPageSize(fallbackPageSize);
            onPageSizeChange(fallbackPageSize);
            onPageIndexChange(1);
        }
    }, [displayPageSize, onPageIndexChange, onPageSizeChange, pageSizeOptions]);

    const handlePageSizeChange = (newPageSize: number) => {
        lastPageSizePropRef.current = newPageSize;
        setDisplayPageSize(newPageSize);
        onPageSizeChange(newPageSize);
        onPageIndexChange(1);
    };

    return (
        <section className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-gray-300 bg-white px-[1.5vw] py-[2.4vh] ${className}`}>
            {filterSlot}

            <div className={`min-h-0 flex-1 overflow-y-auto pr-[0.3vw] ${contentClassName}`}>
                {isLoading ? (
                    <div className={gridClassName}>
                        {Array.from({ length: displayPageSize }).map((_, index) => (
                            <React.Fragment key={index}>
                                {renderSkeleton(index)}
                            </React.Fragment>
                        ))}
                    </div>
                ) : hasItems ? (
                    <div className={gridClassName}>
                        {children}
                    </div>
                ) : (
                    <div className={`flex h-[32vh] items-center justify-center rounded-[12px] border border-dashed border-gray-300 bg-gray-50 text-[1.8vh] font-medium text-gray-500 ${emptyClassName}`}>
                        {emptyMessage}
                    </div>
                )}
            </div>

            {(isLoading || totalItems > 0) && (
                <div className="mt-[2.5vh] flex shrink-0 flex-wrap items-center justify-between gap-[1vw] border-t border-gray-100 pt-[1.8vh]">
                    <p className="text-[1.55vh] font-semibold text-gray-500">
                        {isLoading ? `Loading ${labelPlural}...` : `Showing ${pageStart} to ${pageEnd} of ${totalItems} ${labelPlural}`}
                    </p>

                    <div className="flex items-center gap-[0.6vw]">
                        <select
                            value={displayPageSize}
                            onChange={(event) => handlePageSizeChange(Number(event.target.value))}
                            disabled={isLoading}
                            className="h-[4.2vh] rounded-full border border-gray-300 bg-white px-[0.9vw] text-[1.5vh] font-semibold text-gray-700 outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size} / page</option>
                            ))}
                        </select>

                        <button
                            type="button"
                            disabled={pageIndex === 1 || isLoading}
                            onClick={() => onPageIndexChange(Math.max(1, pageIndex - 1))}
                            className="flex h-[4.2vh] w-[4.2vh] items-center justify-center rounded-full border border-gray-300 text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronLeft size={18} />
                        </button>

                        {pageNumbers.map(page => (
                            <button
                                key={page}
                                type="button"
                                disabled={isLoading}
                                onClick={() => onPageIndexChange(page)}
                                className={`h-[4.2vh] min-w-[4.2vh] rounded-full px-[0.8vw] text-[1.5vh] font-bold ${page === pageIndex ? 'bg-primary-main text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            type="button"
                            disabled={pageIndex >= totalPages || isLoading}
                            onClick={() => onPageIndexChange(Math.min(totalPages, pageIndex + 1))}
                            className="flex h-[4.2vh] w-[4.2vh] items-center justify-center rounded-full border border-gray-300 text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
